import { runtimeMode } from '../../lib/auth';
import { Button } from '@sqi/ui';
import { useRouterState } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { demoDisclaimer } from '../../lib/demo-disclaimer';
import { setLanguage } from '../../i18n';

export interface PublicProposal {
  readonly quotationId: string;
  readonly quotationVersion: number;
  readonly customerName: string;
  readonly projectName: string;
  readonly validityDate: string;
  readonly tiers: readonly {
    readonly quantity: number;
    readonly unitPrice: string;
    readonly currency: string;
  }[];
  readonly terms: readonly {
    readonly labelTh: string;
    readonly labelEn: string;
    readonly value: string;
  }[];
  readonly assumptions: readonly string[];
  readonly exclusions: readonly string[];
  readonly demo: boolean;
}

const demoProposal: PublicProposal = {
  quotationId: 'QT-26001',
  quotationVersion: 3,
  customerName: 'บริษัท ไทรโค จำกัด',
  projectName: 'เซรั่มบำรุงหนังศีรษะ 30 มล.',
  validityDate: '2026-08-31',
  tiers: [
    { quantity: 3_000, unitPrice: '62.00', currency: 'THB' },
    { quantity: 5_000, unitPrice: '49.00', currency: 'THB' },
    { quantity: 10_000, unitPrice: '42.00', currency: 'THB' },
  ],
  terms: [
    { labelTh: 'มัดจำ', labelEn: 'Deposit', value: '50%' },
    { labelTh: 'ระยะเวลาผลิตโดยประมาณ', labelEn: 'Estimated lead time', value: '10–12 weeks' },
  ],
  assumptions: [demoDisclaimer],
  exclusions: ['Government fees and automatic government submission'],
  demo: true,
};

export function PublicProposalPage() {
  const { t, i18n } = useTranslation();
  const token = useRouterState({
    select: (state) => state.location.pathname.split('/').filter(Boolean)[1] ?? '',
  });
  const demoFallbackAllowed = runtimeMode === 'fixture' && token === 'demo-5000-sqi';
  const [proposal, setProposal] = useState<PublicProposal | null>(
    demoFallbackAllowed ? demoProposal : null,
  );
  const [loadState, setLoadState] = useState<'loading' | 'ready' | 'unavailable'>(
    demoFallbackAllowed ? 'ready' : 'loading',
  );
  const [tier, setTier] = useState<number>(demoProposal.tiers[1]?.quantity ?? 5_000);
  const [response, setResponse] = useState<'accept' | 'changes' | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const apiUrl = import.meta.env.VITE_API_URL ?? 'http://localhost:8787';

  useEffect(() => {
    const robots = document.createElement('meta');
    robots.name = 'robots';
    robots.content = 'noindex,nofollow,noarchive';
    document.head.append(robots);
    return () => robots.remove();
  }, []);

  useEffect(() => {
    if (demoFallbackAllowed) return undefined;
    const controller = new AbortController();
    void fetch(`${apiUrl}/v1/public/proposals/${encodeURIComponent(token)}`, {
      cache: 'no-store',
      signal: controller.signal,
    })
      .then(async (result) => {
        if (!result.ok) throw new Error(`HTTP ${result.status}`);
        return (await result.json()) as { readonly data: PublicProposal };
      })
      .then(({ data }) => {
        setProposal(data);
        setTier(data.tiers[0]?.quantity ?? 0);
        setLoadState('ready');
      })
      .catch((error: unknown) => {
        if (error instanceof DOMException && error.name === 'AbortError') return;
        if (!demoFallbackAllowed) {
          setProposal(null);
          setLoadState('unavailable');
        }
      });
    return () => controller.abort();
  }, [apiUrl, demoFallbackAllowed, token]);

  const numberFormatter = useMemo(
    () => new Intl.NumberFormat(i18n.language.startsWith('th') ? 'th-TH' : 'en-US'),
    [i18n.language],
  );
  const moneyFormatter = useMemo(
    () =>
      new Intl.NumberFormat(i18n.language.startsWith('th') ? 'th-TH' : 'en-US', {
        style: 'currency',
        currency: 'THB',
      }),
    [i18n.language],
  );

  const submitResponse = async (form: HTMLFormElement, action: 'accept' | 'changes') => {
    if (proposal === null) return;
    const fields = new FormData(form);
    const name = fields.get('name');
    const role = fields.get('role');
    const comment = fields.get('comment');
    const idempotencyKey = crypto.randomUUID();
    if (demoFallbackAllowed) {
      setResponse(action);
      return;
    }
    setSubmitting(true);
    try {
      const result = await fetch(
        `${apiUrl}/v1/public/proposals/${encodeURIComponent(token)}/responses`,
        {
          method: 'POST',
          headers: { 'content-type': 'application/json', 'idempotency-key': idempotencyKey },
          body: JSON.stringify({
            idempotencyKey,
            action: action === 'accept' ? 'accept' : 'request_changes',
            quotationVersion: proposal.quotationVersion,
            selectedTierQuantity: tier,
            customerName: typeof name === 'string' ? name : '',
            customerRole: typeof role === 'string' ? role : '',
            comment: typeof comment === 'string' ? comment : '',
          }),
        },
      );
      if (!result.ok) throw new Error(`HTTP ${result.status}`);
      setResponse(action);
    } catch {
      if (demoFallbackAllowed) setResponse(action);
    } finally {
      setSubmitting(false);
    }
  };

  if (loadState === 'loading') {
    return (
      <main className="proposal-state" role="status">
        {t('publicProposal.loading')}
      </main>
    );
  }
  if (loadState === 'unavailable' || proposal === null) {
    return (
      <main className="proposal-state">
        <h1>{t('publicProposal.unavailableTitle')}</h1>
        <p>{t('publicProposal.unavailableBody')}</p>
      </main>
    );
  }

  return (
    <main className="proposal-page">
      <header>
        <div className="brand">
          <span className="brand__mark">SQI</span>
          <span>
            <strong>SQI Demo Manufacturing</strong>
            <small>
              {t('publicProposal.documentLabel', {
                id: proposal.quotationId,
                version: proposal.quotationVersion,
              })}
            </small>
          </span>
        </div>
        <select
          aria-label={t('common.language')}
          value={i18n.language.startsWith('en') ? 'en' : 'th'}
          onChange={(event) => void setLanguage(event.target.value === 'en' ? 'en' : 'th')}
        >
          <option value="th">ไทย</option>
          <option value="en">EN</option>
        </select>
      </header>
      <article>
        {proposal.demo ? <p className="eyebrow">{t('common.demo')}</p> : null}
        <h1>{proposal.projectName}</h1>
        <p>{t('publicProposal.preparedFor', { customer: proposal.customerName })}</p>
        <p>{t('publicProposal.validUntil', { date: proposal.validityDate })}</p>
        <section>
          <h2>{t('publicProposal.productConcept')}</h2>
          <p>{t('publicProposal.conceptDescription')}</p>
        </section>
        <section>
          <h2>{t('quotation.selectTier')}</h2>
          <div className="public-tiers">
            {proposal.tiers.map((item) => (
              <label className={tier === item.quantity ? 'selected' : ''} key={item.quantity}>
                <input
                  checked={tier === item.quantity}
                  name="tier"
                  onChange={() => setTier(item.quantity)}
                  type="radio"
                />
                <strong>
                  {t('publicProposal.units', { count: numberFormatter.format(item.quantity) })}
                </strong>
                <span>
                  {t('publicProposal.perUnit', {
                    price: moneyFormatter.format(Number(item.unitPrice)),
                  })}
                </span>
              </label>
            ))}
          </div>
        </section>
        <section className="proposal-terms">
          {proposal.terms.map((term) => (
            <div key={`${term.labelEn}-${term.value}`}>
              <span>{i18n.language.startsWith('th') ? term.labelTh : term.labelEn}</span>
              <strong>{term.value}</strong>
            </div>
          ))}
        </section>
        {proposal.demo ? (
          <div className="demo-banner">
            <strong>{demoDisclaimer}</strong>
          </div>
        ) : null}
        {response === null ? (
          <form
            className="acceptance-form"
            onSubmit={(event) => {
              event.preventDefault();
              void submitResponse(event.currentTarget, 'accept');
            }}
          >
            <label>
              {t('publicProposal.name')}
              <input maxLength={120} name="name" required />
            </label>
            <label>
              {t('publicProposal.role')}
              <input maxLength={120} name="role" />
            </label>
            <label>
              {t('publicProposal.comment')}
              <textarea maxLength={2_000} name="comment" />
            </label>
            <div>
              <Button disabled={submitting} type="submit">
                {t('quotation.accept')}
              </Button>
              <Button
                className="sqi-button--secondary"
                disabled={submitting}
                onClick={(event) => {
                  const form = event.currentTarget.form;
                  if (form?.reportValidity()) void submitResponse(form, 'changes');
                }}
              >
                {t('quotation.requestChanges')}
              </Button>
              <Button
                className="sqi-button--secondary"
                disabled={submitting}
                onClick={() =>
                  void import('./public-proposal-export').then(({ downloadPublicProposal }) =>
                    downloadPublicProposal(proposal, tier),
                  )
                }
              >
                {t('publicProposal.download')}
              </Button>
            </div>
            <small>{t('quotation.publicNotice')}</small>
          </form>
        ) : (
          <div className="response-confirmation" role="status">
            {response === 'accept'
              ? t('publicProposal.accepted')
              : t('publicProposal.changesRecorded')}
          </div>
        )}
      </article>
    </main>
  );
}
